import { pluralizeString } from '@elementary/common-ui/utils/common';
import { AddTestForm } from 'shared/types';

const MIN_ANOMALY_TRAINING_BUCKETS = 7;

const periodInDays: Record<
  AddTestForm['detectionUnit'] | AddTestForm['trainingPeriodUnit'],
  number
> = {
  hour: 1 / 24,
  day: 1,
  week: 7,
  month: 30,
};

export type AnomalyTimeWindow = Pick<
  AddTestForm,
  | 'detectionPeriod'
  | 'detectionUnit'
  | 'trainingPeriodCount'
  | 'trainingPeriodUnit'
  | 'seasonality'
>;

const isPositiveInteger = (value: number) =>
  Number.isInteger(value) && value > 0;

export const getAnomalyTimeWindowError = ({
  detectionPeriod,
  detectionUnit,
  trainingPeriodCount,
  trainingPeriodUnit,
  seasonality,
}: AnomalyTimeWindow): string | undefined => {
  const bucketCount = Number(detectionPeriod);
  const trainingCount = Number(trainingPeriodCount);
  if (!isPositiveInteger(bucketCount) || !isPositiveInteger(trainingCount)) {
    return 'Enter positive whole numbers for the time bucket and comparison window.';
  }

  if (seasonality === 'yes' && (bucketCount !== 1 || detectionUnit !== 'day')) {
    return 'Seasonality requires a 1-day time bucket.';
  }

  const bucketDays = bucketCount * periodInDays[detectionUnit];
  const trainingDays = trainingCount * periodInDays[trainingPeriodUnit];
  const bucketsCovered = Math.floor(trainingDays / bucketDays);
  if (bucketsCovered >= MIN_ANOMALY_TRAINING_BUCKETS) return undefined;

  const minimumDays = Math.ceil(MIN_ANOMALY_TRAINING_BUCKETS * bucketDays);
  return `Compare at least ${MIN_ANOMALY_TRAINING_BUCKETS} time buckets (${pluralizeString(
    minimumDays,
    'day',
    'days',
  )}). This window covers ${bucketsCovered}.`;
};

export const describeAnomalyTimeWindow = ({
  detectionPeriod,
  detectionUnit,
  trainingPeriodCount,
  trainingPeriodUnit,
}: Omit<AnomalyTimeWindow, 'seasonality'>):
  | { bucket: string; trainingPeriod: string }
  | undefined =>
  detectionPeriod && detectionUnit && trainingPeriodCount && trainingPeriodUnit
    ? {
        bucket: `${detectionPeriod}-${detectionUnit}`,
        trainingPeriod: pluralizeString(
          trainingPeriodCount,
          trainingPeriodUnit,
          `${trainingPeriodUnit}s`,
        ),
      }
    : undefined;
